/**
 * Calculation Service for Dashboard
 * Handles all statistical calculations from IndexedDB data
 */

import * as indexedDBService from './indexedDBService';
import * as cacheService from './cacheService';
import * as versionService from './versionService';

/**
 * Get dashboard data with caching
 * @param {number} groupId - Group ID
 * @param {string} metricType - Type of metric (record_count, column_breakdown, daily_growth)
 * @param {Object} options - Additional options
 * @returns {Promise<any>}
 */
export const getDashboardData = async (groupId, metricType, options = {}) => {
    try {
        // 1. Get current version from server
        const currentVersion = await versionService.getCurrentGroupVersion(groupId);
        
        // 2. Generate cache key
        const cacheKey = cacheService.generateCacheKey(groupId, metricType, currentVersion, options);
        
        // 3. Check local cache
        const cached = await cacheService.getCachedData(cacheKey);
        
        // 4. Return cached if valid
        if (cached && cached.data) {
            console.log(`[Calculation] Using cached data for ${metricType}`);
            return cached.data;
        }
        
        // 5. Calculate from IndexedDB
        console.log(`[Calculation] Calculating ${metricType} from IndexedDB...`);
        const result = await calculateMetric(groupId, metricType, options);
        
        // 6. Store in cache
        await cacheService.setCachedData(cacheKey, {
            group_id: groupId,
            metric_type: metricType,
            version: currentVersion,
            data: result,
            expires_at: Date.now() + (options.cacheTTL || 5 * 60 * 1000), // Default 5 minutes
            ...(options.column_name && { column_name: options.column_name })
        });
        
        return result;
    } catch (error) {
        console.error('[Calculation] Error getting dashboard data:', error);
        throw error;
    }
};

/**
 * Calculate a specific metric
 * @param {number} groupId - Group ID
 * @param {string} metricType - Type of metric
 * @param {Object} options - Calculation options
 * @returns {Promise<any>}
 */
const calculateMetric = async (groupId, metricType, options) => {
    switch (metricType) {
        case 'record_count':
            return await calculateRecordCount(groupId, options);
        
        case 'column_breakdown':
            return await calculateColumnBreakdown(groupId, options);
        
        case 'daily_growth':
            return await calculateDailyGrowth(groupId, options);
        
        case 'overview':
            return await calculateOverview(groupId, options);
        
        default:
            throw new Error(`Unknown metric type: ${metricType}`);
    }
};

/**
 * Calculate total record count
 * @param {number} groupId - Group ID
 * @param {Object} options - Options
 * @returns {Promise<number>}
 */
const calculateRecordCount = async (groupId, options) => {
    const storeName = options.storeName || `group_${groupId}_data`;
    
    try {
        const count = await indexedDBService.countRecordsInGroup(groupId, storeName);
        return count;
    } catch (error) {
        console.error('[Calculation] Error calculating record count:', error);
        return 0;
    }
};

/**
 * Calculate column breakdown
 * @param {number} groupId - Group ID
 * @param {Object} options - Options with column_name
 * @returns {Promise<Array>}
 */
const calculateColumnBreakdown = async (groupId, options) => {
    if (!options.column_name) {
        throw new Error('column_name is required for column breakdown');
    }
    
    const storeName = options.storeName || `group_${groupId}_data`;
    
    try {
        const breakdown = await indexedDBService.getColumnBreakdown(storeName, options.column_name);
        return breakdown;
    } catch (error) {
        console.error('[Calculation] Error calculating column breakdown:', error);
        return [];
    }
};

/**
 * Calculate daily growth
 * @param {number} groupId - Group ID
 * @param {Object} options - Options with days count
 * @returns {Promise<Array>}
 */
const calculateDailyGrowth = async (groupId, options) => {
    const storeName = options.storeName || `group_${groupId}_data`;
    const days = options.days || 30;
    const dateField = options.dateField || 'created_at';
    
    try {
        const growth = await indexedDBService.getDailyGrowth(storeName, days, dateField);
        
        // Calculate summary statistics
        const counts = growth.map(item => item.count);
        const total = counts.reduce((sum, count) => sum + count, 0);
        const avg = total / counts.length;
        const max = Math.max(...counts);
        const min = Math.min(...counts);
        
        return {
            daily_data: growth,
            summary: {
                total,
                average: Math.round(avg * 100) / 100,
                peak: max,
                lowest: min,
                days: days
            }
        };
    } catch (error) {
        console.error('[Calculation] Error calculating daily growth:', error);
        return {
            daily_data: [],
            summary: { total: 0, average: 0, peak: 0, lowest: 0, days }
        };
    }
};

/**
 * Calculate overview (combined metrics)
 * @param {number} groupId - Group ID
 * @param {Object} options - Options
 * @returns {Promise<Object>}
 */
const calculateOverview = async (groupId, options) => {
    try {
        const recordCount = await calculateRecordCount(groupId, options);
        const dailyGrowthData = await calculateDailyGrowth(groupId, { ...options, days: 7 });
        
        // Calculate unique users count (count unique values in "Name" field if exists)
        let userCount = 0;
        try {
            const storeName = `group_${groupId}_data`;
            const records = await indexedDBService.getAllRecords(storeName);
            
            const uniqueNames = new Set();
            records.forEach(record => {
                // Check various possible user field names
                const userName = record.Name || record.name || record.user_name || record.username || record.User;
                if (userName && userName !== 'NA' && userName.trim()) {
                    uniqueNames.add(userName);
                }
            });
            userCount = uniqueNames.size;
        } catch (err) {
            console.error('[Calculation] Error calculating user count:', err);
        }
        
        return {
            total_records: recordCount,
            recent_growth: dailyGrowthData.summary.total,
            average_daily: dailyGrowthData.summary.average,
            user_count: userCount
        };
    } catch (error) {
        console.error('[Calculation] Error calculating overview:', error);
        return {
            total_records: 0,
            recent_growth: 0,
            average_daily: 0,
            user_count: 0
        };
    }
};

/**
 * Invalidate cached calculations for a group
 * @param {number} groupId - Group ID
 * @returns {Promise<void>}
 */
export const invalidateGroupCalculations = async (groupId) => {
    try {
        await cacheService.invalidateGroupCache(groupId);
        console.log(`[Calculation] Invalidated calculations for group ${groupId}`);
    } catch (error) {
        console.error('[Calculation] Error invalidating calculations:', error);
    }
};

/**
 * Recalculate all metrics for a group (force refresh)
 * @param {number} groupId - Group ID
 * @param {Object} options - Calculation options
 * @returns {Promise<Object>}
 */
export const recalculateAllMetrics = async (groupId, options = {}) => {
    try {
        // Invalidate cache first
        await invalidateGroupCalculations(groupId);
        
        // Recalculate all metrics
        const [recordCount, dailyGrowth] = await Promise.all([
            getDashboardData(groupId, 'record_count', options),
            getDashboardData(groupId, 'daily_growth', options)
        ]);
        
        return {
            record_count: recordCount,
            daily_growth: dailyGrowth
        };
    } catch (error) {
        console.error('[Calculation] Error recalculating metrics:', error);
        throw error;
    }
};

/**
 * Get available columns for breakdown
 * @param {number} groupId - Group ID
 * @param {Object} options - Options
 * @returns {Promise<Array<string>>}
 */
export const getAvailableColumns = async (groupId, options = {}) => {
    const storeName = options.storeName || `group_${groupId}_data`;
    
    try {
        // Get a sample record to determine available columns
        const records = await indexedDBService.getAllRecords(storeName);
        
        if (records.length === 0) {
            return [];
        }
        
        // Get keys from first record
        const columns = Object.keys(records[0]).filter(key => {
            // Filter out internal keys and timestamps
            return !key.startsWith('_') && !key.includes('timestamp');
        });
        
        return columns;
    } catch (error) {
        console.error('[Calculation] Error getting available columns:', error);
        return [];
    }
};

/**
 * Calculate custom aggregation
 * @param {number} groupId - Group ID
 * @param {Function} aggregateFn - Custom aggregation function
 * @param {Object} options - Options
 * @returns {Promise<any>}
 */
export const calculateCustomAggregation = async (groupId, aggregateFn, options = {}) => {
    const storeName = options.storeName || `group_${groupId}_data`;
    
    try {
        const records = await indexedDBService.getAllRecords(storeName);
        return aggregateFn(records);
    } catch (error) {
        console.error('[Calculation] Error calculating custom aggregation:', error);
        throw error;
    }
};
